import axios, { AxiosInstance, AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios'
import storage, { storagekeys } from '@/utils/storage'
import { getStorageUserInfo } from '@/business/login'
import { triggerGotoLoginExpiredEvent } from '@/business/login/help'
import { getCompanyExId, tokenKey } from '@/utils/user'
import { getServersDomain } from '@/servers/config'
import { clearLoginState } from '@/business/login'
import { message } from 'antd'
import { $t } from '@/i18n/intl'
import { isFunction } from '@/utils'
import { countryEnum } from '@/const'
import { getStores } from '@/stores'

//需要过滤的错误
const whiteListErrorList = ['timeout of 5000ms exceeded']

interface ResultData<T = any> {
  data: T
  code: number
  message: string
  request_id: string
  reason?: string
}

const URL = getServersDomain()

export enum RequestEnums {
  OVERDUE = 4, // 登录过期
  OVERDUE_NEW = 3, // 登录过期
  OVERDUE_TOKEN = 401, // token过期
  NO_TOKEN = 2, //无token
  NO_AUTH = 55, //无权限
  BUSY = 9, //登录占线
  TOKEN_ERROR = 5, //token鉴权失败
  FAIL = 999, // 请求失败
  SUCCESS = 0, // 请求成功
  TIMEOUT = 120000
}

const config = {
  // 默认地址
  baseURL: URL,
  // 设置超时时间
  timeout: RequestEnums.TIMEOUT,
  // 跨域时候允许携带凭证
  withCredentials: false,
  headers: {
    'Content-Type': 'application/json; charset=UTF-8;'
  }
}

const requestDataCacheMap = new Map()

class RequestHttp {
  // 定义成员变量并指定类型
  service: AxiosInstance
  public constructor(config: AxiosRequestConfig) {
    // 实例化axios
    this.service = axios.create(config)
    this.service.interceptors.request.use(
      (config: any) => {
        const data = getStorageUserInfo()
        const { userStore } = getStores()
        const token = data?.userInfo?.token || userStore.userInfo.token || ''
        const companyId = getCompanyExId()
        const country = data?.country || countryEnum.CN
        const lang = storage.get(storagekeys.currentLang)

        requestDataCacheMap.set(config.url, config)

        return {
          ...config,
          headers: {
            // port,
            [tokenKey]: token,
            LoginType: 'aigc',
            lang: lang,
            CompanyExID: companyId,
            // ai-agent 服务（如 /ai_agent/market/*）读取小写 header，与 SSE 一致
            companyexid: companyId,
            country: country,
            ...(config.headers || {})
          }
        }
      },
      (error: AxiosError) => {
        if (!whiteListErrorList.includes(error.message)) {
          console.error(error)
        }
        Promise.reject(error)
      }
    )

    /**
     * 响应拦截器
     */
    this.service.interceptors.response.use(
      (response: AxiosResponse) => {
        const { data, headers } = response

        if (
          headers?.['content-type'] ===
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        ) {
          return response
        }
        return this.handleSuccCode(data.code, data)

        // 全局错误信息拦截（防止下载文件得时候返回数据流，没有code，直接报错）
        // if (data.code && data.code !== RequestEnums.SUCCESS) {
        //   return Promise.reject(data)
        // }
      },
      (error: AxiosError) => {
        const { response, message, config } = error
        const requestData = requestDataCacheMap.get(config?.url) || undefined
        if (requestData) {
          requestDataCacheMap.delete(config?.url)
        }

        if (!whiteListErrorList.includes(error.message)) {
          console.error(message, error)
        }

        if (response) {
          return this.handleErrorCode(response.status)
        }
        return Promise.reject(error)
      }
    )
  }

  async handleSuccCode(code: number, data: ResultData): Promise<any | undefined> {
    switch (code) {
      case RequestEnums.OVERDUE:
      case RequestEnums.NO_TOKEN:
      case RequestEnums.NO_AUTH:
      case RequestEnums.BUSY:
      case RequestEnums.OVERDUE_TOKEN:
      case RequestEnums.OVERDUE_NEW:
      case RequestEnums.TOKEN_ERROR:
        if (data.message) {
          const win = window as any
          if (isFunction(win?.antd_toast)) {
            win.antd_toast(data.message)
          }
        }
        clearLoginState()
        triggerGotoLoginExpiredEvent()
        return Promise.reject(data)
      default:
        return Promise.resolve(data)
    }
  }

  handleErrorCode(code: number): Promise<number> {
    switch (code) {
      case 404:
      case 501:
      case 502:
        message.error($t('const.server_error_tips'))
        return Promise.reject(code)
      case 401:
        return Promise.reject(code)
      default:
        return Promise.reject(code)
    }
  }

  // 常用方法封装
  get<T>(url: string, params?: object, conf: AxiosRequestConfig = {}): Promise<ResultData<T>> {
    return this.service.get(url, { ...conf, params })
  }
  post<T>(url: string, params?: object, config: AxiosRequestConfig = {}): Promise<ResultData<T>> {
    return this.service.post(url, params, config)
  }
  put<T>(url: string, params?: object): Promise<ResultData<T>> {
    return this.service.put(url, params)
  }
  delete<T>(url: string, params?: object): Promise<ResultData<T>> {
    return this.service.delete(url, { params })
  }
}

// 导出一个实例对象
export default new RequestHttp(config)
